import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormControlLabel, Switch, Autocomplete, TextField, Button, Box, Card } from '@mui/material';
import { fetchTicketureAllEventIDs, fetchTicketureByEventIDs, fetchTicketureByOpenDate } from '../services/ticketure';
import { excludeCancelledEvents } from '../utils/excludeCancelledEvents';
import SkeletonLoader from './SkeletonLoader';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';

const Dashboard = () => {

  const [selectedEventIDs, setSelectedEventIDs] = useState([]);
  const [appliedEventIDs, setAppliedEventIDs] = useState([]);
  const [showCumulative, setShowCumulative] = useState(false);
  const [chartData, setChartData] = useState({ series: [] });
  const [eventData, setEventData] = useState([])

  const dispatch = useDispatch();
  const state = useSelector((state) => state.ticketure);
  const companyName = useSelector((state) => state.company.company);
  const startDate = dayjs().subtract(1, 'day').startOf('week').format('YYYY-MM-DD');
  const endDate = dayjs().add(7, 'day').endOf('week').format('YYYY-MM-DD');

  const handlePasteEventIDs = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
  
    // Acceptable delimiters: comma, newline, tab, semicolon
    const rawIDs = pastedText
      .split(/[,;\n\t]+/)
      .map(id => id.trim())
      .filter(Boolean);
  
    // Validate against available event IDs
    const validEventIDs = rawIDs.filter(id => state?.allEventIds?.includes(id));
    const invalidEventIDs = rawIDs.filter(id => !state?.allEventIds?.includes(id));
  
    // Combine with existing selections, removing duplicates
    const combinedIDs = Array.from(new Set([...selectedEventIDs, ...validEventIDs]));
    setSelectedEventIDs(combinedIDs);
  
    if (invalidEventIDs.length > 0) {
      alert(`Some Event IDs were not recognized and were not added: \n${invalidEventIDs.join(', ')}`);
    }
  };

  // Fetch event data based on companyName
  useEffect(() => {
    setChartData({ series: [] }); // Clear the chart when company changes
    dispatch(fetchTicketureAllEventIDs());
    dispatch(fetchTicketureByOpenDate({ startDate, endDate }));
    console.log(endDate)
  }, [dispatch, startDate, endDate, companyName]);

  // Set applied event IDs
  useEffect(() => {
    if (state?.defaultEventIDs && state?.defaultEventIDs.length > 0) {
      const uniqueEventIDs = Array.from(new Set(state?.defaultEventIDs.map(event => event.event_id)));
      const validEventIDs = excludeCancelledEvents(uniqueEventIDs);
      setAppliedEventIDs(validEventIDs);
    }
  }, [state?.defaultEventIDs]);

  // Process chart data and handle multiple event IDs
  useEffect(() => {
    if (state?.eventData && state?.eventData.length > 0) {
      const rawData = eventData;
      const processedData = processChartData(rawData);
      setChartData(processedData);
    }
  }, [state?.eventData, showCumulative]);

  const handleToggleChange = () => {
    setShowCumulative(!showCumulative);
  };

  useEffect(() => {
    if (appliedEventIDs.length > 0) {
      const fetchAllEventData = async () => {
        const allEventData = await Promise.all(appliedEventIDs.map(eventID => dispatch(fetchTicketureByEventIDs(eventID))));
        const rawData = allEventData.flatMap((response) => response.payload);
        const processedData = processChartData(rawData);
        setChartData(processedData);
        setEventData(rawData);
      };
      fetchAllEventData();
    }
  }, [dispatch, appliedEventIDs, companyName]);

  const processChartData = (data) => {
    if (!data) return { series: [] };

    const groupedData = data.reduce((acc, item) => {
      if (!acc[item.event_id]) {
        acc[item.event_id] = new Map();
      }
      const yValue = showCumulative ? item.cumulative_tickets : item.daily_ticket_count;
      const ordersValue = showCumulative ? item.total_orders : item.daily_orders;
      const merchValue = showCumulative ? item.cumulative_merch : item.daily_merch_count;
      acc[item.event_id].set(item.days_out, {
        y: yValue,
        sale_date: item.sale_date,
        ordersValue: ordersValue,
        merchValue: merchValue,
      });
      return acc;
    }, {});

    const series = Object.keys(groupedData).map(event_id => {
      const sortedData = Array.from(groupedData[event_id].entries())
        .sort((a, b) => a[0] - b[0])
        .map(([x, { y, sale_date, ordersValue, merchValue }]) => ({ x, y, sale_date, ordersValue, merchValue }));

      return {
        name: event_id,
        data: sortedData,
      };
    });

    return { series };
  };

  const handleApply = () => {
    setAppliedEventIDs([...selectedEventIDs]);
  };

  const chartOptions = {
    chart: {
      id: 'mainChart',
      type: 'line',
      toolbar: { show: true },
      zoom: { enabled: false },
    },
    markers: { size: 3, strokeWidth: 1 },
    stroke: { width: 3, curve: 'smooth', lineCap: 'round' },
    xaxis: {
      title: { text: 'Days Out' },
      type: 'numeric',
      labels: { formatter: (value) => Math.floor(value) },
      min: -2,
      max: 60,
    },
    yaxis: {
      title: { text: showCumulative ? 'Cumulative Ticket Count' : 'Daily Ticket Count' },
      min: 0,
    },
    title: { text: 'Ticket Sales Over Time', align: 'left' },
    legend: { position: 'top', horizontalAlign: 'left' },
    tooltip: {
      enabled: true,
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const dataPoint = w.config.series[seriesIndex].data[dataPointIndex];
        const daysOut = dataPoint.x;
        const ticketCount = dataPoint.y;
        const merchCount = dataPoint.merchValue;
        const saleDate = dataPoint.sale_date;
        const orderCount = dataPoint.ordersValue;
  
        return `
          <div style="padding: 10px; border: 1px solid #ccc; background: white;">
            <strong>Event: ${w.config.series[seriesIndex].name}</strong><br />
            Days Out: ${daysOut}<br />
            Sale Date: ${saleDate}<br />
            Ticket Count: ${ticketCount}<br />
            Add Ons: ${merchCount}<br />
            Orders: ${orderCount}
          </div>
        `;
      },
    },
  };

  const brushChartOptions = {
    chart: {
      id: 'brushChart',
      height: 130,
      type: 'area',
      brush: {
        target: 'mainChart',
        enabled: true,
      },
      selection: {
        enabled: true,
        xaxis: {
          min: -2,
          max: 60,
        },
      },
    },
    xaxis: {
      type: 'numeric',
      labels: { formatter: (value) => Math.floor(value) },
    },
    yaxis: {
      tickAmount: 2,
    },
  };

  return (
    <div style={{ margin: '10px 30px', background: '#F1F5F9' }}>
      <Card>
        <Box sx={{ p: 3, pb: 1 }} dir="ltr">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ padding: '10px' }}>
              <FormControlLabel control={<Switch checked={showCumulative} onChange={handleToggleChange} />} label="Show Cumulative Sales" />
            </div>
            <Autocomplete
              multiple
              options={state?.allEventIds || []}
              getOptionLabel={option => option}
              value={selectedEventIDs}
              onChange={(event, newValue) => setSelectedEventIDs(newValue)}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select Event IDs"
                  onPaste={handlePasteEventIDs}
                />
              )}
              style={{ width: '70%' }}
            />
            <Button variant="contained" color="primary" onClick={handleApply}>Apply</Button>
          </div>
          {state.loading || !state?.eventData ? (
            <SkeletonLoader loading={state?.loading || !state?.eventData} width="100%" height={450} />
          ) : (
            <>
              <Chart options={chartOptions} series={chartData.series} type="line" height={450} style={{ marginTop: 20 }} />
              <Chart options={brushChartOptions} series={chartData.series} type="area" height={130} />
            </>
          )}
        </Box>
      </Card>
    </div>
  );
};

export default Dashboard;